(() => {
  if (window.tfgOctaneCartCompatibility) return;

  // Octane AI adds quiz products with /cart/add.js and only updates the cart count bubble.
  // This refreshes the theme's cart drawer (and anything listening for cart updates) afterwards.
  const quizSelector = '#octane-quiz-outer-wrapper';
  const quizEmbedSelector = '.octane-ai-quiz, .octane-ai-quiz-rendered, #octane-quiz-outer-wrapper';
  const pollDelays = [150, 250, 400, 700, 1100, 1600, 2400];
  let knownCartFingerprint = null;
  let watchRequestId = 0;

  function wait(delay) {
    return new Promise((resolve) => window.setTimeout(resolve, delay));
  }

  function getDisplayedCartCount() {
    const countElement = document.querySelector('#cart-icon-bubble .cart-count-bubble span[aria-hidden="true"]');
    if (!countElement) return null;

    const count = Number.parseInt(countElement.textContent || '', 10);
    return Number.isNaN(count) ? null : count;
  }

  function getCartFingerprint(cart) {
    if (!cart || !Array.isArray(cart.items)) return null;

    return JSON.stringify({
      itemCount: cart.item_count,
      items: cart.items.map((item) => [item.key || item.variant_id || item.id, item.quantity]),
    });
  }

  function getCart() {
    const cartUrl = window.routes?.cart_url || '/cart';
    const separator = cartUrl.includes('?') ? '&' : '?';

    return fetch(`${cartUrl}.js${separator}_tfg_octane_cart=${Date.now()}`, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    }).then((response) => {
      if (!response.ok) throw new Error(`Cart request failed with status ${response.status}.`);
      return response.json();
    });
  }

  function refreshAndOpenCartDrawer(cart) {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || typeof cartDrawer.renderContents !== 'function') return Promise.resolve(false);

    const cartUrl = window.routes?.cart_url || '/cart';
    const sections = typeof cartDrawer.getSectionsToRender === 'function'
      ? cartDrawer.getSectionsToRender().map((section) => section.id).filter(Boolean)
      : ['cart-drawer', 'cart-icon-bubble'];
    const sectionUrl =
      cartUrl +
      '?sections=' +
      encodeURIComponent(sections.join(',')) +
      '&sections_url=' +
      encodeURIComponent(window.location.pathname || cartUrl) +
      '&_tfg_octane_drawer=' +
      Date.now();

    // No "Accept: application/json" header here: with it, Shopify answers /cart?sections= with the
    // cart JSON instead of the rendered sections.
    return fetch(sectionUrl, {
      credentials: 'same-origin',
      cache: 'no-store',
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Cart drawer request failed with status ${response.status}.`);
        return response.json();
      })
      .then((sectionsHtml) => {
        const hasAllSections = sections.every((sectionId) => typeof sectionsHtml?.[sectionId] === 'string');
        if (!hasAllSections) throw new Error('Shopify did not return all required cart drawer sections.');

        // Same steps as the theme's own add to cart (product-form.js), so the free shipping
        // announcement and the drawer's empty state update too.
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'octane-quiz', cartData: cart });
        }

        cartDrawer.__tfgSkipDiscountRefresh = true;
        cartDrawer.renderContents({
          id: cart?.items?.[cart.items.length - 1]?.variant_id || null,
          sections: sectionsHtml,
        });
        if (cart?.item_count > 0) cartDrawer.classList.remove('is-empty');
        window.setTimeout(() => {
          cartDrawer.__tfgSkipDiscountRefresh = false;
        }, 0);

        return true;
      });
  }

  async function watchForConfirmedCartChange() {
    const requestId = ++watchRequestId;
    const baselineFingerprint = knownCartFingerprint;
    const baselineDisplayedCount = getDisplayedCartCount();

    for (const delay of pollDelays) {
      await wait(delay);
      if (requestId !== watchRequestId) return false;

      let cart;
      try {
        cart = await getCart();
      } catch (error) {
        continue;
      }

      if (requestId !== watchRequestId) return false;

      const currentFingerprint = getCartFingerprint(cart);
      const cartChanged = baselineFingerprint !== null
        ? currentFingerprint !== baselineFingerprint
        : baselineDisplayedCount !== null && cart.item_count !== baselineDisplayedCount;

      if (!cartChanged) continue;

      knownCartFingerprint = currentFingerprint;

      try {
        return await refreshAndOpenCartDrawer(cart);
      } catch (error) {
        console.error('Unable to refresh the cart drawer after the Octane AI cart changed.', error);
        return false;
      }
    }

    return false;
  }

  function isOctaneAddToCartClick(event) {
    const button = event.target instanceof Element ? event.target.closest('button') : null;
    if (!button || !button.closest(quizSelector)) return false;

    const label = [button.textContent, button.getAttribute('title'), button.getAttribute('aria-label')]
      .filter(Boolean)
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();

    return /\badd\b.*\b(cart|bag)\b/i.test(label);
  }

  function initializeCartSnapshot() {
    // Only pages with an embedded quiz need a starting snapshot. Elsewhere (popup quizzes) the
    // cart count bubble is the baseline, which saves a cart request on every page view.
    if (!document.querySelector(quizEmbedSelector)) return;

    getCart()
      .then((cart) => {
        knownCartFingerprint = getCartFingerprint(cart);
      })
      .catch(() => {});
  }

  document.addEventListener(
    'click',
    (event) => {
      if (isOctaneAddToCartClick(event)) watchForConfirmedCartChange();
    },
    true
  );

  document.addEventListener('octane.quiz.addToCart', watchForConfirmedCartChange, false);
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) initializeCartSnapshot();
  });
  initializeCartSnapshot();

  window.tfgOctaneCartCompatibility = {
    refresh: watchForConfirmedCartChange,
  };
})();
